/**
 * The landing sample's variant choices — a colour, a thickness (KAN-1559).
 *
 * The values come from the SELECTED platform material (owner, 2026-09-23: the
 * platform material is the one source), exactly like in a shop: an option
 * field bound through ``options_source.material_property`` offers the
 * material's ``option_sources[<property>]``, the operation's numeric axis
 * (``variant_axis.key``) its ``variant_axis_values``. The shop's form reads both
 * off ``catalog.materials``; ``sampleCatalog`` is the minimal catalog that
 * gives it the same data, so the landing draws the shop's own picker.
 */

import type { Catalog, MaterialOptionPublic } from "@/api/types";

export interface SampleMaterial {
  code: string;
  display_name: string;
  option_sources?: Record<string, MaterialOptionPublic[]>;
  variant_axis_values?: Record<string, number[]>;
}

export function sampleCatalog(
  lang: string,
  processType: string,
  materials: SampleMaterial[],
): Catalog {
  return {
    lang,
    materials: materials.map((m) => ({
      code: m.code,
      display_name: m.display_name,
      description: null,
      features: null,
      density_kg_m3: 0,
      finish_codes: [],
      option_sources: m.option_sources ?? {},
      variant_axis_values: m.variant_axis_values ?? {},
      process_types: [processType],
    })),
    finishes: [],
    delivery_options: [],
  };
}

function boundProperty(field: Record<string, unknown>): string | null {
  const source = field.options_source;
  if (!source || typeof source !== "object" || Array.isArray(source)) return null;
  const key = (source as { material_property?: unknown }).material_property;
  return typeof key === "string" && key ? key : null;
}

/**
 * Picks the material offers for every variant choice whose current value it
 * does not — the first offered value, like the shop's form after a material
 * switch. A value still offered is never touched, so the visitor's own pick
 * survives. Returns only the keys that change.
 */
export function seedVariantChoices(
  fields: Array<Record<string, unknown>>,
  material: SampleMaterial | undefined,
  axisKey: string | null | undefined,
  value: Record<string, unknown>,
): Record<string, unknown> {
  if (!material) return {};
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    const key = typeof field.key === "string" ? field.key : "";
    const prop = boundProperty(field);
    if (!key || !prop) continue;
    const codes = (material.option_sources?.[prop] ?? []).map((o) => o.code);
    if (codes.length > 0 && !codes.includes(String(value[key] ?? ""))) {
      out[key] = codes[0];
    }
  }
  if (axisKey) {
    const offered = material.variant_axis_values?.[axisKey] ?? [];
    const current = value[axisKey];
    if (offered.length > 0 && !(typeof current === "number" && offered.includes(current))) {
      out[axisKey] = offered[0];
    }
  }
  return out;
}
